import { useRef, useState } from 'react';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { storage } from '../firebase';
import { snapshotPickedFiles } from '../lib/attachments';

const MAX_BYTES = 25 * 1024 * 1024; // must match storage.rules
const DOCUMENT_ACCEPT =
  '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const DOCUMENT_EXTENSIONS = /\.(pdf|doc|docx)$/i;

/**
 * Uploads to matters/{matterId}/{category}/ and stores the metadata on the matter
 * document. Every upload keeps its own timestamped path and is never overwritten —
 * re-uploading a corrected engagement letter or conflicts check adds a new dated entry
 * rather than replacing the old one, so there's a record of what changed and when if a
 * matter gets kicked back for a document issue. `category` defaults to 'other' so any
 * existing callers (or previously-uploaded files with no category) keep working.
 */
export default function Attachments({
  matterId,
  category = 'other',
  value = [],
  onChange,
  disabled,
  documentsOnly = false,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);
  const files = Array.isArray(value) ? value : [];

  const uploadFiles = async (pickedFiles) => {
    const picked = Array.from(pickedFiles || []);
    if (!picked.length) return;

    setBusy(true);
    setError('');
    try {
      const uploaded = [];
      for (const file of picked) {
        if (documentsOnly && !DOCUMENT_EXTENSIONS.test(file.name)) {
          throw new Error(`${file.name} is not a supported document. Choose a PDF, DOC, or DOCX file.`);
        }
        if (file.size > MAX_BYTES) {
          throw new Error(`${file.name} is larger than the 25 MB limit.`);
        }
        const timestamp = Date.now();
        const path = `matters/${matterId}/${category}/${timestamp}-${file.name}`;
        const storageRef = ref(storage, path);
        // eslint-disable-next-line no-await-in-loop
        await uploadBytes(storageRef, file, { contentType: file.type });
        // eslint-disable-next-line no-await-in-loop
        const url = await getDownloadURL(storageRef);
        uploaded.push({
          path,
          url,
          name: file.name,
          size: file.size,
          contentType: file.type,
          category,
          uploadedAt: new Date(timestamp).toISOString(),
        });
      }
      onChange([...files, ...uploaded]);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const upload = (event) => {
    // FileList is live. It must be copied before clearing the input to allow selecting
    // the same file again; otherwise clearing `.value` empties the list before the
    // asynchronous uploader can read it.
    const picked = snapshotPickedFiles(event.target.files);
    event.target.value = '';
    uploadFiles(picked);
  };

  return (
    <div className="sm:col-span-2">
      {files.length > 0 && (
        <ul className="mb-2 divide-y divide-slate-100 rounded border border-slate-200 dark:border-slate-700">
          {files.map((file) => (
            <li key={file.path} className="flex items-center justify-between px-3 py-2 text-sm">
              <a
                href={file.url}
                target="_blank"
                rel="noreferrer"
                className="text-firm-700 dark:text-firm-100 hover:underline"
              >
                {file.name}
              </a>
              <span className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                {file.uploadedAt && (
                  <span title={file.uploadedAt}>
                    {new Date(file.uploadedAt).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </span>
                )}
                {(file.size / 1024).toFixed(0)} KB
                {!disabled && (
                  <button
                    type="button"
                    className="text-red-600 dark:text-red-400 hover:underline"
                    onClick={() => onChange(files.filter((f) => f.path !== file.path))}
                  >
                    Remove
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {!disabled && (
        <div
          className={`rounded border-2 border-dashed p-4 text-center transition-colors ${
            isDragging
              ? 'border-firm-500 bg-firm-50 dark:border-firm-300 dark:bg-firm-900'
              : 'border-slate-300 dark:border-slate-600'
          }`}
          onDragEnter={(event) => {
            event.preventDefault();
            if (!busy) setIsDragging(true);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={(event) => {
            event.preventDefault();
            setIsDragging(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setIsDragging(false);
            if (!busy) uploadFiles(event.dataTransfer.files);
          }}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={documentsOnly ? DOCUMENT_ACCEPT : undefined}
            disabled={busy}
            onChange={upload}
            className="sr-only"
          />
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Drag and drop {documentsOnly ? 'a document' : 'files'} here
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {documentsOnly ? 'PDF, DOC, or DOCX files' : 'Multiple files'} up to 25 MB each.
          </p>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
            >
              {files.length > 0 ? 'Browse to replace or add files' : 'Browse files'}
            </button>
          </div>
        </div>
      )}
      {busy && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Uploading…</p>}
      {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
